import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Image as ImageIcon, Wand2, Save, Loader } from 'lucide-react';
import './EventDecorationChat.css';

const EventDecorationChat = ({ event, onClose, onSave }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi! I'm your event decoration assistant. I can help you customize the styling of "${event?.title || 'your event'}" page. \n\nTell me what you'd like - colors, fonts, backgrounds, layouts, or upload images for inspiration, and I'll create custom CSS to make it happen!`
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [generatedCSS, setGeneratedCSS] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setUploadedImages(prev => [...prev, {
            file,
            url: reader.result,
            id: Date.now() + Math.random()
          }]);
        };
        reader.readAsDataURL(file);
      }
    });
    e.target.value = ''; // Reset input
  };

  const removeImage = (id) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
  };

  const generateCSSFromPrompt = async (prompt, images) => {
    // This will call an API endpoint that uses OpenAI or similar
    // For now, I'll create a mock implementation that you can replace with actual API call
    
    try {
      const response = await fetch('/api/v1/ai/generate-css', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          images: images.map(img => img.url),
          eventContext: {
            title: event?.title,
            theme: event?.theme,
            currentColors: event?.customColors
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate CSS');
      }

      const data = await response.json();
      return data.css;
    } catch (error) {
      console.error('Error generating CSS:', error);
      // Fallback: Generate basic CSS based on prompt
      return generateFallbackCSS(prompt);
    }
  };

  const generateFallbackCSS = (prompt) => {
    // Simple fallback CSS generator - can be enhanced
    const promptLower = prompt.toLowerCase();
    let css = '/* Generated CSS for event page decoration */\n\n';
    
    if (promptLower.includes('dark') || promptLower.includes('black')) {
      css += `.event-details-page { background: #000000; color: #ffffff; }\n`;
    } else if (promptLower.includes('light') || promptLower.includes('white')) {
      css += `.event-details-page { background: #ffffff; color: #000000; }\n`;
    }
    
    if (promptLower.includes('purple') || promptLower.includes('violet')) {
      css += `.secret-event-celebrating, .secret-event-location { border-left-color: #9b59b6; }\n`;
      css += `h2 { color: #9b59b6; }\n`;
    }
    
    if (promptLower.includes('blue')) {
      css += `.secret-event-celebrating, .secret-event-location { border-left-color: #3498db; }\n`;
      css += `h2 { color: #3498db; }\n`;
    }
    
    if (promptLower.includes('gradient')) {
      css += `.event-details-hero { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }\n`;
    }
    
    return css || '/* No styling changes detected in your request */';
  };

  const handleSend = async () => {
    if (!input.trim() && uploadedImages.length === 0) return;

    const userMessage = {
      role: 'user',
      content: input,
      images: uploadedImages.map(img => img.url)
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Generate CSS from the prompt
    try {
      const css = await generateCSSFromPrompt(input, uploadedImages);
      setGeneratedCSS(css);
      
      // Apply CSS temporarily
      applyCSS(css);

      const assistantMessage = {
        role: 'assistant',
        content: `I've generated custom styling for your event page! The changes are being applied. You can see the preview now. If you'd like adjustments, just tell me what to change.`
      };

      setMessages(prev => [...prev, assistantMessage]);
      setUploadedImages([]); // Clear uploaded images after processing
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: `I encountered an error generating the styling. Please try again or be more specific about what you'd like.`
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyCSS = (css) => {
    const styleId = `event-decoration-${event?.id || 'temp'}`;
    let styleElement = document.getElementById(styleId);
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    styleElement.textContent = css;
  };

  const handleSave = () => {
    if (onSave && generatedCSS) {
      onSave({
        customCSS: generatedCSS,
        customColors: extractColorsFromCSS(generatedCSS)
      });
      onClose();
    }
  };

  const extractColorsFromCSS = (css) => {
    // Extract color values from CSS (simple extraction)
    const colors = {};
    const primaryMatch = css.match(/border-left-color:\s*(#[0-9a-fA-F]{6}|[a-z]+)/);
    const bgMatch = css.match(/background:\s*(#[0-9a-fA-F]{6}|[a-z]+)/);
    
    if (primaryMatch) colors.primary = primaryMatch[1];
    if (bgMatch) colors.background = bgMatch[1];
    
    return Object.keys(colors).length > 0 ? colors : null;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="decoration-chat-overlay">
      <div className="decoration-chat-container">
        <div className="decoration-chat-header">
          <div className="chat-header-title">
            <Wand2 size={24} />
            <h2>AI Event Decoration</h2>
          </div>
          <button className="chat-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="decoration-chat-messages">
          {messages.map((message, index) => (
            <div key={index} className={`chat-message ${message.role}`}>
              {message.images && message.images.length > 0 && (
                <div className="message-images">
                  {message.images.map((imgUrl, imgIndex) => (
                    <img key={imgIndex} src={imgUrl} alt="Uploaded" className="message-image" />
                  ))}
                </div>
              )}
              <div className="message-content">{message.content}</div>
            </div>
          ))}
          {isLoading && (
            <div className="chat-message assistant">
              <div className="message-content">
                <Loader className="spinning" size={20} />
                Generating your custom styling...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {uploadedImages.length > 0 && (
          <div className="uploaded-images-preview">
            {uploadedImages.map((img) => (
              <div key={img.id} className="preview-image-container">
                <img src={img.url} alt="Preview" className="preview-image" />
                <button 
                  className="remove-image-btn"
                  onClick={() => removeImage(img.id)}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="decoration-chat-input">
          <button
            className="upload-image-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Upload images"
          >
            <ImageIcon size={20} />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
          />
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Describe how you want to decorate the page... (e.g., 'Make it dark with purple accents' or 'Use a gradient background')"
            rows={2}
            className="chat-textarea"
          />
          <button
            onClick={handleSend}
            disabled={isLoading || (!input.trim() && uploadedImages.length === 0)}
            className="send-btn"
          >
            <Send size={20} />
          </button>
        </div>

        {generatedCSS && (
          <div className="decoration-chat-actions">
            <button onClick={handleSave} className="save-btn">
              <Save size={18} />
              Save Decoration
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDecorationChat;

